import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RoundManagement() {
  const [roundNumber, setRoundNumber] = useState(1);
  const [headsUpSeconds, setHeadsUpSeconds] = useState(60);
  const [durationSeconds, setDurationSeconds] = useState(60);
  const [activeRound, setActiveRound] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/round-status`, { withCredentials: true });
      if (res.data.status !== 'idle') {
        setActiveRound(res.data);
      } else {
        setActiveRound(null);
      }
    } catch {}
  };

  const startRound = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/start-round?round_number=${roundNumber}&heads_up_seconds=${headsUpSeconds}&duration_seconds=${durationSeconds}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Round ${roundNumber} started! Players will be notified.`);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start round');
    } finally {
      setLoading(false);
    }
  };

  const endRound = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/end-round`, {}, { withCredentials: true });
      toast.success('Round ended!');
      setActiveRound(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end round');
    } finally {
      setLoading(false);
    }
  };

  const isActive = activeRound && (activeRound.status === 'upcoming' || activeRound.status === 'in_progress');

  return (
    <div className="border border-blue-900/50 bg-blue-950/20 p-6">
      <h3 className="font-heading font-bold text-xl mb-4 text-blue-400">Round Management</h3>

      {isActive ? (
        <div>
          <div className="bg-zinc-900 border border-zinc-700 p-4 mb-4">
            <p className="text-white font-bold">Round {activeRound.round?.round_number} &mdash; <span className={activeRound.status === 'in_progress' ? 'text-red-400' : 'text-yellow-400'}>{activeRound.status === 'in_progress' ? 'IN PROGRESS' : 'UPCOMING'}</span></p>
            {activeRound.status === 'upcoming' && activeRound.countdown_seconds && (
              <p className="text-zinc-400 text-sm mt-1">Starts in {activeRound.countdown_seconds}s</p>
            )}
            {activeRound.status === 'in_progress' && activeRound.remaining_seconds && (
              <p className="text-zinc-400 text-sm mt-1">{activeRound.remaining_seconds}s remaining</p>
            )}
          </div>
          <button
            onClick={endRound}
            disabled={loading}
            data-testid="end-round-btn"
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 font-bold uppercase transition-colors disabled:opacity-50"
          >
            {loading ? 'Ending...' : 'End Round Now'}
          </button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-zinc-400 mb-4">Start a race round. Players will receive a heads-up notification, then selections & boosts lock during the round.</p>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Round Number</label>
              <input
                type="number"
                min="1"
                value={roundNumber}
                onChange={(e) => setRoundNumber(parseInt(e.target.value) || 1)}
                data-testid="round-number-input"
                className="w-full bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Heads-up (seconds)</label>
              <input
                type="number"
                min="10"
                value={headsUpSeconds}
                onChange={(e) => setHeadsUpSeconds(parseInt(e.target.value) || 60)}
                data-testid="heads-up-input"
                className="w-full bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Duration (seconds)</label>
              <input
                type="number"
                min="10"
                value={durationSeconds}
                onChange={(e) => setDurationSeconds(parseInt(e.target.value) || 60)}
                data-testid="duration-input"
                className="w-full bg-zinc-950 border border-zinc-800 h-10 px-3 text-white"
              />
            </div>
          </div>
          <button
            onClick={startRound}
            disabled={loading}
            data-testid="start-round-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-bold uppercase transition-colors disabled:opacity-50"
          >
            {loading ? 'Starting...' : `Start Round ${roundNumber}`}
          </button>
        </div>
      )}
    </div>
  );
}
